import { useState } from 'react'
import { Link, NavLink, Outlet } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LanguageSwitch } from './LanguageSwitch'
import { ScrollToTop } from './ScrollToTop'
import { paths } from '@/lib/paths'
import { useLocale } from '@/lib/useLocale'

export function Layout() {
  const locale = useLocale()
  const { t } = useTranslation()
  const [menuOpen, setMenuOpen] = useState(false)

  const navItems = [
    { to: paths.home(locale), label: t('nav.home'), end: true },
    { to: paths.gallery(locale), label: t('nav.gallery'), end: false },
    { to: paths.about(locale), label: t('nav.about'), end: false },
    { to: paths.contacts(locale), label: t('nav.contacts'), end: false },
  ]

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />

      {/* Keyboard users land here first and can jump past the navigation. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded focus:bg-stone-900 focus:px-4 focus:py-2 focus:text-white"
      >
        {t('nav.skipToContent')}
      </a>

      <header className="sticky top-0 z-40 border-b border-stone-200/70 bg-stone-50/85 backdrop-blur">
        <div className="container-page flex h-16 items-center justify-between gap-4">
          <Link
            to={paths.home(locale)}
            className="font-[family-name:var(--font-display)] text-xl tracking-tight"
          >
            {t('site.name')}
          </Link>

          <nav aria-label={t('nav.menu')} className="hidden items-center gap-7 md:flex">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `text-sm transition-colors ${
                    isActive ? 'text-stone-900' : 'text-stone-500 hover:text-stone-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSwitch current={locale} />
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              className="-mr-2 p-2 md:hidden"
            >
              <span className="sr-only">{menuOpen ? t('nav.close') : t('nav.menu')}</span>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.6" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="1.6" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav
            id="mobile-menu"
            aria-label={t('nav.menu')}
            className="border-t border-stone-200 bg-stone-50 md:hidden"
          >
            <div className="container-page flex flex-col py-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  // Closing here rather than in an effect on the pathname keeps
                  // the cause (a tap) next to the result.
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-stone-100 py-3 text-sm last:border-0 ${
                      isActive ? 'text-stone-900' : 'text-stone-500'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-stone-200 py-10">
        <div className="container-page flex flex-col gap-4 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {t('site.name')}. {t('footer.rights')}.
          </p>
          <p className="text-stone-400">{t('footer.madeWith')}</p>
        </div>
      </footer>
    </div>
  )
}
